import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Chip,
  Slider,
  IconButton,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';
import { RelationType, CreateRelationshipForm, KnowledgeNode } from '../../types/knowledge';

interface AddConnectionModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (relationshipData: CreateRelationshipForm) => Promise<void>;
  sourceNode: KnowledgeNode | null;
  targetNode: KnowledgeNode | null;
  availableNodes: KnowledgeNode[];
  existingRelationships: Array<{ source_node_id: string; target_node_id: string; relation_type: RelationType }>;
}

const relationTypeDescriptions: Record<RelationType, string> = {
  'DESCRIBED_BY': 'The target describes or explains the source',
  'REQUIRES': 'The source requires the target to function',
  'REALIZED_BY': 'The source is realized or implemented by the target',
  'USES': 'The source uses or depends on the target',
  'PRODUCES': 'The source produces or creates the target',
  'CONSUMES': 'The source consumes or uses up the target',
  'SATISFIES': 'The source satisfies or fulfills the target',
  'MITIGATES': 'The source reduces or prevents the target',
  'MEASURED_BY': 'The source is measured or evaluated by the target',
  'OWNED_BY': 'The source is owned or controlled by the target',
  'RELATED_TO': 'The source is generally related to the target',
  'HAS_EXPERTISE': 'The source has expertise in the target',
  'WORKS_WITH': 'The source works with or collaborates with the target',
  'CREATED': 'The source created or authored the target',
  'SPECIALIZES_IN': 'The source specializes in the target area',
  'MAINTAINS': 'The source maintains or supports the target',
  'FOCUSES_ON': 'The source focuses on or concentrates on the target',
  'IMPLEMENTS': 'The source implements or executes the target',
  'USED_FOR': 'The source is used for the target purpose',
  'ENABLES': 'The source enables or makes possible the target',
  'DEPLOYS': 'The source deploys or utilizes the target',
  'DESCRIBES': 'The source describes or documents the target',
  'DOCUMENTS': 'The source documents or records the target',
  'COLLABORATES_WITH': 'The source collaborates with the target',
};

const relationTypeColors: Record<RelationType, string> = {
  'DESCRIBED_BY': '#4CAF50',
  'REQUIRES': '#FF9800',
  'REALIZED_BY': '#9C27B0',
  'USES': '#795548',
  'PRODUCES': '#2196F3',
  'CONSUMES': '#607D8B',
  'SATISFIES': '#3F51B5',
  'MITIGATES': '#F44336',
  'MEASURED_BY': '#00BCD4',
  'OWNED_BY': '#E91E63',
  'RELATED_TO': '#9E9E9E',
  'HAS_EXPERTISE': '#2196F3',
  'WORKS_WITH': '#4CAF50',
  'CREATED': '#FF9800',
  'SPECIALIZES_IN': '#9C27B0',
  'MAINTAINS': '#607D8B',
  'FOCUSES_ON': '#795548',
  'IMPLEMENTS': '#3F51B5',
  'USED_FOR': '#00BCD4',
  'ENABLES': '#8BC34A',
  'DEPLOYS': '#FF5722',
  'DESCRIBES': '#009688',
  'DOCUMENTS': '#673AB7',
  'COLLABORATES_WITH': '#E91E63',
};

export function AddConnectionModal({ 
  open, 
  onClose, 
  onSubmit, 
  sourceNode, 
  targetNode, 
  availableNodes,
  existingRelationships 
}: AddConnectionModalProps) {
  const [formData, setFormData] = useState<CreateRelationshipForm>({
    source_node_id: '',
    target_node_id: '',
    relation_type: 'RELATED_TO',
    strength: 0.5,
    description: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (sourceNode && targetNode) {
      setFormData(prev => ({
        ...prev,
        source_node_id: sourceNode.id,
        target_node_id: targetNode.id,
      }));
    }
  }, [sourceNode, targetNode]);

  const handleSubmit = async () => {
    if (!formData.source_node_id || !formData.target_node_id) {
      setError('Please select both source and target nodes');
      return;
    }

    if (formData.source_node_id === formData.target_node_id) {
      setError('Source and target nodes cannot be the same');
      return;
    }

    // Check for existing relationship
    const existingRelationship = existingRelationships.find(
      rel => rel.source_node_id === formData.source_node_id && 
             rel.target_node_id === formData.target_node_id && 
             rel.relation_type === formData.relation_type
    );

    if (existingRelationship) {
      setError('This relationship already exists');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      await onSubmit(formData);
      handleClose();
    } catch (error) {
      console.error('Error creating relationship:', error);
      setError('Failed to create relationship');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      source_node_id: '',
      target_node_id: '',
      relation_type: 'RELATED_TO',
      strength: 0.5,
      description: '',
    });
    setError(null);
    onClose();
  };

  const getNodeById = (id: string) => availableNodes.find(node => node.id === id);

  const selectedSourceNode = getNodeById(formData.source_node_id);
  const selectedTargetNode = getNodeById(formData.target_node_id);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Create Connection</Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Source Node Selection */}
          <FormControl fullWidth>
            <InputLabel>Source Node</InputLabel>
            <Select
              value={formData.source_node_id}
              onChange={(e) => setFormData(prev => ({ ...prev, source_node_id: e.target.value }))}
              label="Source Node"
              disabled={!!sourceNode} // Disable if pre-selected
            >
              {availableNodes.map((node) => (
                <MenuItem key={node.id} value={node.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: node.color,
                      }}
                    />
                    <Typography>{node.name}</Typography>
                    <Chip label={node.type} size="small" variant="outlined" />
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Relationship Type */}
          <FormControl fullWidth>
            <InputLabel>Relationship Type</InputLabel>
            <Select
              value={formData.relation_type}
              onChange={(e) => setFormData(prev => ({ ...prev, relation_type: e.target.value as RelationType }))}
              label="Relationship Type"
            >
              {Object.entries(relationTypeDescriptions).map(([type, description]) => (
                <MenuItem key={type} value={type}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: relationTypeColors[type as RelationType],
                        }}
                      />
                      <Typography variant="body2" fontWeight="bold">
                        {type.replace('_', ' ')}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {description}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Target Node Selection */}
          <FormControl fullWidth>
            <InputLabel>Target Node</InputLabel>
            <Select
              value={formData.target_node_id}
              onChange={(e) => setFormData(prev => ({ ...prev, target_node_id: e.target.value }))}
              label="Target Node"
              disabled={!!targetNode} // Disable if pre-selected
            >
              {availableNodes.map((node) => (
                <MenuItem key={node.id} value={node.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: node.color,
                      }}
                    />
                    <Typography>{node.name}</Typography>
                    <Chip label={node.type} size="small" variant="outlined" />
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Connection Strength */}
          <Box>
            <Typography gutterBottom>
              Connection Strength: {Math.round(formData.strength * 100)}%
            </Typography>
            <Slider
              value={formData.strength}
              onChange={(_, value) => setFormData(prev => ({ ...prev, strength: value as number }))}
              min={0.1}
              max={1}
              step={0.1}
              marks={[
                { value: 0.1, label: 'Weak' },
                { value: 0.5, label: 'Medium' },
                { value: 1, label: 'Strong' },
              ]}
            />
          </Box>

          {/* Description */}
          <TextField
            label="Description (Optional)"
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            fullWidth
            multiline
            rows={2}
            placeholder="Add additional context about this relationship"
          />

          {/* Preview */}
          {selectedSourceNode && selectedTargetNode && (
            <Box>
              <Typography gutterBottom>Connection Preview</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: Math.sqrt(selectedSourceNode.size) * 2,
                      height: Math.sqrt(selectedSourceNode.size) * 2,
                      borderRadius: '50%',
                      backgroundColor: selectedSourceNode.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '0.8rem',
                      minWidth: 32,
                      minHeight: 32,
                    }}
                  >
                    {selectedSourceNode.name.charAt(0)}
                  </Box>
                  <Typography variant="body2" fontWeight="bold">
                    {selectedSourceNode.name}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 4,
                      height: 4,
                      borderRadius: '50%',
                      backgroundColor: relationTypeColors[formData.relation_type],
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {formData.relation_type.replace('_', ' ')}
                  </Typography>
                  <ArrowIcon sx={{ fontSize: 16, color: relationTypeColors[formData.relation_type] }} />
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" fontWeight="bold">
                    {selectedTargetNode.name}
                  </Typography>
                  <Box
                    sx={{
                      width: Math.sqrt(selectedTargetNode.size) * 2,
                      height: Math.sqrt(selectedTargetNode.size) * 2,
                      borderRadius: '50%',
                      backgroundColor: selectedTargetNode.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '0.8rem',
                      minWidth: 32,
                      minHeight: 32,
                    }}
                  >
                    {selectedTargetNode.name.charAt(0)}
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!formData.source_node_id || !formData.target_node_id || loading}
        >
          {loading ? 'Creating...' : 'Create Connection'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
